import React from 'react'
import Card from '../../../common/Card'
import {
  INDIVIDUAL_PROVIDER_TYPE,
  capitalizeFirstLetter
} from '../../../../consts'
import { ProviderLanguages } from '../../../../consts/ProviderConsts'
import { useSelector } from 'react-redux'
import { isNil } from 'ramda'
import { Tooltip } from 'antd'
import $t from '../../../../i18n'
import FamilyMemberLabel from '../../../common/FamilyMemberLabel'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import {
  getAntidoteProviderCopay,
  getNetworkCopayAccordingToProvider
} from '../../../../utils/hmoUtils'
import { getContractPeriod, getProviderSpecialities } from '../../../../utils/providerSearchUtils'

// images
import providerAvatar from 'resources/generic-doctor.svg'
import buildings from 'resources/buildings.png'
import ProviderAction from '../ProviderAction/ProviderAction'
import ProviderTier from '../../../common/ProviderTier/ProviderTier'

import './ProviderItem.css'

// ProviderItem is a component that displays a provider's information
const ProviderItem = ({
  provider,
  onClick,
  providerTypeHeader,
  isFirstAvailableProvider,
  isFamily,
  patientId,
  hideNewMembersIndication,
  shouldShowDistance = true,
  shouldShowCTA,
  isPcpChangeFlow
}) => {
  const memberCopay = useSelector(state => state?.nhMember?.memberCopay)
  const providersSearchEnableYearFilter = useSelector(
    state => state.providers.providersSearchEnableYearFilter
  )
  const isLocationExist =
    provider?.address || provider?.city || provider?.state || provider?.zipcode
  const availableFormat = 'MM/DD hh:mm A'
  const isCareCoordinator = provider?.care_coordinator
  const phoneNumber = provider?.provider_numbers?.find(
    number => number?.number_type === 'phone'
  )?.number

  const networkCopay = getNetworkCopayAccordingToProvider(memberCopay, provider)

  const filteredLanguages = provider?.languages?.filter(
    language => language?.name !== ProviderLanguages.ENGLISH
  )

  const copay =
    !isFirstAvailableProvider && provider?.is_antidote
      ? provider?.copay || getAntidoteProviderCopay(memberCopay, provider)
      : networkCopay?.copay

  dayjs.extend(isSameOrAfter)

  const shouldShowStartDateLabel =
    !!provider?.start_date && !dayjs.utc().isSameOrAfter(provider?.start_date)

  const onCardClick = e => {
    if (!onClick) return null

    console.log(e.target.parent)
    e?.stopPropagation()
    onClick()
  }

  return (
    <>
      <Card
        className={
          onClick
            ? 'providers-provider-item'
            : 'providers-provider-item no-pointer'
        }
        onClick={onCardClick}
      >
        {provider?.end_date && (
          <div className='provider-date'>
            {$t('profile.careTeam.pcpEndDate', {
              date: dayjs(provider?.end_date).format('MMM D, YYYY')
            })}
          </div>
        )}
        {shouldShowStartDateLabel && (
          <div className='provider-date'>
            {$t('profile.careTeam.pcpStartDate', {
              date: dayjs(provider?.start_date).format('MMM D, YYYY')
            })}
          </div>
        )}
        <div className='provider-title-container'>
          {!isFirstAvailableProvider && (
            <img
              src={
                provider?.provider_type === INDIVIDUAL_PROVIDER_TYPE
                  ? providerAvatar
                  : buildings
              }
              className='provider-img'
              alt=''
            />
          )}
          <div className='provider-title'>
            {providerTypeHeader && (
              <div className='provider-header grey tiny'>
                {providerTypeHeader}:
              </div>
            )}
            <div className='semi-bold'>
              {provider?.prac_category_code
                ? provider?.name + ', ' + provider?.prac_category_code
                : provider?.name}
            </div>
            <div className='provider-desc grey'>
              {getProviderSpecialities(provider)}
            </div>

            {isCareCoordinator && (
              <div className='provider-desc grey'>{provider?.email}</div>
            )}
            {provider?.tier && (
              <ProviderTier tier={provider.tier} textSize='tiny' />
            )}
          </div>
          <div className='provider-dates-patient-container'>
            {providersSearchEnableYearFilter &&
              getContractPeriod(
                provider?.contract_start_date,
                provider?.contract_end_date
              ).map((year, index) => (
                <div key={year + index} className='contract-year'>
                  {$t('providerPage.providerDetails.inNetworkFor', { year })}
                </div>
              ))}
            {isFamily && patientId && (
              <FamilyMemberLabel patientId={patientId} />
            )}
          </div>
        </div>
        {provider?.accept_new_patients !== null &&
          !hideNewMembersIndication && (
            <div
              className={
                provider?.accept_new_patients
                  ? 'highlighted-provider'
                  : 'highlighted-provider alert'
              }
            >
              {provider?.accept_new_patients
                ? $t('providerPage.providerDetails.newMembers')
                : $t('providerPage.providerDetails.NoNewMembers')}
            </div>
          )}
        <div className='provider-details'>
          {!!provider?.gender && (
            <div className='provider-details-row'>
              <i className='icon-profile' />
              <div className='provider-desc'>
                <span className='semi-bold'>
                  {capitalizeFirstLetter(provider?.gender)}
                </span>
              </div>
            </div>
          )}
          {!!filteredLanguages && filteredLanguages.length > 0 && (
            <div className='provider-details-row'>
              <i className='icon-language' />
              <div className='provider-desc'>
                <span className='semi-bold'>
                  {$t('providerPage.providerDetails.speaks')}:{' '}
                </span>
                {filteredLanguages?.map(language => language?.name)?.join(', ')}
              </div>
            </div>
          )}
          {!!phoneNumber && (
            <div className='provider-details-row'>
              <i className='icon-phone' />
              <div className='provider-desc'>{phoneNumber}</div>
            </div>
          )}
          {!!provider?.telehealth && provider?.next_availablility && (
            <div className='provider-details-row'>
              <i className='icon-calendar' />
              <div className='provider-desc'>
                <span className='semi-bold'>
                  {$t('providerPage.providerDetails.nextAvailability')}:{' '}
                </span>{' '}
                {dayjs(provider?.next_availablility).format(availableFormat)}
              </div>
            </div>
          )}
          {!!provider?.telehealth && (
            <div className='provider-details-row'>
              <i className='icon-video' />
              <div className='provider-desc semi-bold'>
                {$t('providerPage.providerDetails.telehealth')}
              </div>
            </div>
          )}
          <div className='provider-extra-container'>
            {!provider?.is_antidote && isLocationExist && (
              <div className='provider-details-row'>
                <i className='icon-location' />
                <div className='provider-desc'>
                  <div>{provider?.address}</div>
                  {
                    //join all string with ', ' if they are not empty
                    [provider?.city, provider?.state, provider?.zipcode]
                      .filter(val => !!val)
                      .join(', ')
                  }
                </div>
              </div>
            )}
            {shouldShowDistance && provider?.proximity && (
              <div className='provider-location'>
                <i className='icon-Location' />
                <div className='provider-location-text semi-bold provider-desc'>
                  {provider?.proximity?.toFixed(1) + ' mi'}
                </div>
              </div>
            )}
          </div>

          <div
            className={
              // copay can be 0
              !isNil(copay)
                ? 'provider-details-footer'
                : 'provider-details-footer end'
            }
          >
            {!isNil(copay) && (
              <div className='copay' onClick={e => e?.stopPropagation()}>
                <span className='semi-bold'>
                  {networkCopay?.isCopayMinRate && !provider?.is_antidote
                    ? $t('providerPage.providerDetails.copayMin', { copay })
                    : '$' + copay}
                </span>
                {$t('providerPage.providerDetails.copay')}
                {networkCopay?.isCopayMinRate && !provider?.is_antidote && (
                  <Tooltip
                    title={$t('providerPage.providerDetails.copayNote', {
                      copay: networkCopay?.copayMaxRate
                    })}
                    placement='bottom'
                    color={'var(--color_primary_t600)'}
                  >
                    <i className='icon-info'></i>
                  </Tooltip>
                )}
              </div>
            )}
            {shouldShowCTA && (
              <ProviderAction
                type='outline'
                className='provider-item-action'
                provider={provider}
                isFirstAvailableProvider={isFirstAvailableProvider}
                isPcpChangeFlow={isPcpChangeFlow}
              />
            )}
          </div>
        </div>
      </Card>
    </>
  )
}

export default ProviderItem
